import React, { memo, useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import CicCommentTemplatePicker from 'app/shared/components/CicCommentTemplatePicker';
import { cicCommentTemplatesPropTypes } from 'app/shared/components/CicCommentTemplatePicker/CicCommentTemplatePicker.propTypes';
import CicValueChange from 'app/shared/components/CicValueChange';
import FieldHelper from 'app/shared/components/FieldHelper';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { CIC_SAVE_NOTE_MAX_LENGTH } from 'constants/cicReason';
import { formatCurrencyValue } from 'utils/format';

import {
  APPLY_AS_CURE_LABEL,
  APPLY_CURE_BUTTON_LABEL,
  CURE_SELECTED_MESSAGE,
  NEW_FEE_LABEL,
  NO_LINKED_REASONS_MESSAGE,
  NOTE_LABEL,
  NOTE_PLACEHOLDER,
  REASON_LABEL,
  REASON_PLACEHOLDER,
  REMOVE_CURE_BUTTON_LABEL,
} from '../../CicFieldReasonsForm.constants';
import { CicFieldReasonsFormTestIds } from '../../CicFieldReasonsForm.testIds';
import { getFeeNoteError, getFeeReasonError } from '../../CicFieldReasonsForm.utils';
import {
  inputsStyles,
  reasonHelpStyles,
  rowStyles,
  summaryStyles,
} from '../CicFieldReasonRow/CicFieldReasonRow.styles';
import CicReasonOption from '../CicReasonOption';

import { noLinkedReasonStyles } from './CicFeeReasonRow.styles';

const CicFeeReasonRow = ({
  feeChange,
  reasons,
  value,
  isTouched,
  onReasonChange,
  onNoteChange,
  onCureChange,
  onBlur,
}) => {
  const { feeTypeId, feeName, oldAmount, newAmount } = feeChange;

  const selectedReason = useMemo(
    () => reasons.find(({ cicFeeReasonId }) => cicFeeReasonId === value.cicReasonId) ?? null,
    [reasons, value.cicReasonId],
  );

  const hasReasons = !_.isEmpty(reasons);
  const isNewFee = _.isNil(oldAmount);
  const reasonError = isTouched ? getFeeReasonError(value) : '';
  const noteError = isTouched ? getFeeNoteError(value) : '';
  const reasonHelp = reasonError || selectedReason?.description;

  const onReasonSelect = useCallback(
    (e, option) => onReasonChange(feeTypeId, option?.cicFeeReasonId ?? null),
    [feeTypeId, onReasonChange],
  );

  const onNoteInput = useCallback(
    e => onNoteChange(feeTypeId, e.target.value),
    [feeTypeId, onNoteChange],
  );

  const onTemplateApply = useCallback(
    text => onNoteChange(feeTypeId, text),
    [feeTypeId, onNoteChange],
  );

  const onCureToggle = useCallback(
    e => onCureChange(feeTypeId, e.target.checked),
    [feeTypeId, onCureChange],
  );

  const onCureButtonClick = useCallback(
    () => onCureChange(feeTypeId, !value.isCure),
    [feeTypeId, onCureChange, value.isCure],
  );

  const onFieldBlur = useCallback(() => onBlur(feeTypeId), [feeTypeId, onBlur]);

  return (
    <Box data-testid={CicFieldReasonsFormTestIds.getFeeRowId(feeTypeId)} sx={rowStyles}>
      <Box sx={summaryStyles}>
        <Typography variant="bold14">{feeName}</Typography>

        <CicValueChange
          newDisplay={formatCurrencyValue(newAmount)}
          oldDisplay={formatCurrencyValue(oldAmount)}
        />

        {isNewFee && <LabelCDS size="S" title={NEW_FEE_LABEL} variant="BLUE" />}
      </Box>

      <Box sx={inputsStyles}>
        {!hasReasons && (
          <Box sx={noLinkedReasonStyles}>
            <Typography
              color={value.isCure ? 'text.secondary' : 'error.main'}
              data-testid={CicFieldReasonsFormTestIds.getFeeNoLinkedReasonsId(feeTypeId)}
              variant="regular14"
            >
              {value.isCure ? CURE_SELECTED_MESSAGE : NO_LINKED_REASONS_MESSAGE}
            </Typography>

            <Button
              data-testid={CicFieldReasonsFormTestIds.getFeeCureButtonId(feeTypeId)}
              variant={value.isCure ? 'outlined' : 'contained'}
              onClick={onCureButtonClick}
            >
              {value.isCure ? REMOVE_CURE_BUTTON_LABEL : APPLY_CURE_BUTTON_LABEL}
            </Button>
          </Box>
        )}

        {hasReasons && (
          <>
            <AutocompleteCDS
              disableClearable
              OptionContent={CicReasonOption}
              dataTestId={CicFieldReasonsFormTestIds.getFeeReasonSelectId(feeTypeId)}
              dataTestIdInput={CicFieldReasonsFormTestIds.getFeeReasonInputId(feeTypeId)}
              dataTestIdOption={CicFieldReasonsFormTestIds.getFeeReasonOptionId(feeTypeId)}
              disabled={value.isCure}
              error={!!reasonError}
              label={REASON_LABEL}
              name={`${feeTypeId}.cicReasonId`}
              optionKey="cicFeeReasonId"
              options={reasons}
              placeholder={REASON_PLACEHOLDER}
              required={!value.isCure}
              value={selectedReason}
              variant="outlined"
              withSortedOptions={false}
              onBlur={onFieldBlur}
              onChange={onReasonSelect}
            />

            {!!reasonHelp && (
              <Typography
                color={reasonError ? 'error.main' : 'text.secondary'}
                data-testid={CicFieldReasonsFormTestIds.getFeeReasonHelpId(feeTypeId)}
                sx={reasonHelpStyles}
                variant="regular12"
              >
                {reasonHelp}
              </Typography>
            )}

            <CicCommentTemplatePicker
              commentTemplates={selectedReason?.commentTemplates}
              dataTestId={CicFieldReasonsFormTestIds.getFeeTemplateSelectId(feeTypeId)}
              dataTestIdInput={CicFieldReasonsFormTestIds.getFeeTemplateInputId(feeTypeId)}
              dataTestIdOption={CicFieldReasonsFormTestIds.getFeeTemplateOptionId(feeTypeId)}
              isDisabled={value.isCure}
              name={`${feeTypeId}.commentTemplate`}
              reasonId={value.cicReasonId}
              onApply={onTemplateApply}
            />

            <Box>
              <TextFieldCDS
                multiline
                dataTestId={CicFieldReasonsFormTestIds.getFeeNoteInputId(feeTypeId)}
                disabled={value.isCure}
                error={!!noteError}
                label={NOTE_LABEL}
                maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
                name={`${feeTypeId}.note`}
                placeholder={NOTE_PLACEHOLDER}
                required={!!value.cicReasonId && !value.isCure}
                rows={3}
                value={value.note}
                variant="outlined"
                onBlur={onFieldBlur}
                onChange={onNoteInput}
              />
              <FieldHelper
                error={noteError}
                length={value.note.length}
                maxLength={CIC_SAVE_NOTE_MAX_LENGTH}
                text=""
              />
            </Box>

            <CheckboxCDS
              dataTestId={CicFieldReasonsFormTestIds.getFeeCureCheckboxId(feeTypeId)}
              label={APPLY_AS_CURE_LABEL}
              name={`${feeTypeId}.isCure`}
              value={value.isCure}
              onChange={onCureToggle}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

CicFeeReasonRow.propTypes = {
  feeChange: PropTypes.shape({
    feeTypeId: PropTypes.string.isRequired,
    feeName: PropTypes.string,
    oldAmount: PropTypes.number,
    newAmount: PropTypes.number,
  }).isRequired,
  isTouched: PropTypes.bool.isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      cicFeeReasonId: PropTypes.string.isRequired,
      name: PropTypes.string,
      description: PropTypes.string,
      commentTemplates: cicCommentTemplatesPropTypes,
    }),
  ).isRequired,
  value: PropTypes.shape({
    cicReasonId: PropTypes.string,
    note: PropTypes.string,
    isCure: PropTypes.bool,
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
  onCureChange: PropTypes.func.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  onReasonChange: PropTypes.func.isRequired,
};

export default memo(CicFeeReasonRow);
